import dayjs from 'dayjs';
import { t } from 'i18next';
import { upperFirst } from 'lodash';
import * as yup from 'yup';

import { ICompanySettings } from 'auth/types';
import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import { PricingHelper } from 'pricing/helpers';
import { PricingStrategy } from 'quotes/enums';
import { getPurchaseCostLabel } from 'quotes/utils';
import { upsertTaxonomyGroupSchemaFields } from 'taxonomy/components';

import { QuotePartUpdateBulkField } from './enums';

const editFieldSchemas = Object.values(QuotePartUpdateBulkField).reduce((acc, value) => {
  acc[`edit${upperFirst(value)}`] = yup.boolean().nullable();

  return acc;
}, {});

const atLeastOneEditField = () =>
  yup.object().test('at-least-one-edit-field', t('errorRequiredAtLeastOneField'), function (value) {
    return Object.values(QuotePartUpdateBulkField).some(
      (field) => value[`edit${upperFirst(field)}`]
    );
  });

export const quotePartUpdateBulkSchema = (
  companySettings: ICompanySettings,
  isAlternateVersionEnabled: boolean
) => {
  const purchaseCostLabel = getPurchaseCostLabel(isAlternateVersionEnabled);

  return yup
    .object()
    .shape({
      ...editFieldSchemas,
      manufacturer: yup
        .object()
        .label(t('labelManufacturer'))
        .nullable()
        .when('editManufacturer', {
          is: true,
          then: yup
            .object()
            .label(t('labelManufacturer'))
            .required(getEmptyErrorMessage(t('labelManufacturer'))),
        }),
      uom: yup
        .string()
        .label(t('labelUom'))
        .nullable()
        .when('editUom', {
          is: true,
          then: yup
            .string()
            .label(t('labelUom'))
            .required(getEmptyErrorMessage(t('labelUom'))),
        }),
      quantity: numberSchemaWithTransform()
        .label(t('labelQuantity'))
        .nullable()
        .when('editQuantity', {
          is: true,
          then: numberSchemaWithTransform().label(t('labelQuantity')).nullable().required(),
        }),
      purchaseCost: numberSchemaWithTransform()
        .label(purchaseCostLabel)
        .nullable()
        .when('editPurchaseCost', {
          is: true,
          then: numberSchemaWithTransform().label(purchaseCostLabel).nullable().required(),
        }),
      vendorQuoteNumber: yup
        .string()
        .label(t('labelVendorQuoteNumber'))
        .nullable()
        .when('editVendorQuoteNumber', {
          is: true,
          then: yup.string().label(t('labelVendorQuoteNumber')).nullable().required(),
        }),
      replacementCost: numberSchemaWithTransform()
        .label(t('labelReplacementCost'))
        .nullable()
        .when('editReplacementCost', {
          is: true,
          then: numberSchemaWithTransform().label(t('labelReplacementCost')).nullable().required(),
        }),
      replacementCostNumber: yup
        .string()
        .label(t('labelReplacementCostNumber'))
        .nullable()
        .when('editReplacementCostNumber', {
          is: true,
          then: yup.string().label(t('labelReplacementCostNumber')).nullable().required(),
        }),
      prioritizeLegacyCost: yup.boolean().label(t('labelPrioritizeLegacyCost')).nullable(),
      price: numberSchemaWithTransform()
        .label(t('labelPrice'))
        .nullable()
        .when('editPrice', {
          is: true,
          then: numberSchemaWithTransform().label(t('labelPrice')).nullable().required(),
        }),
      pricingDate: yup
        .string()
        .label(t('labelPricingDate'))
        .nullable()
        .test('future-date', t('errorPricingDateMustBeFuture'), (value) => {
          if (!value) {
            return true;
          }

          const selectedDate = dayjs(value).startOf('day');
          const today = dayjs().startOf('day');

          return selectedDate.valueOf() > today.valueOf();
        }),
      pricingStrategy: yup
        .string()
        .label(t('labelPricingStrategy'))
        .oneOf(Object.values(PricingStrategy))
        .nullable()
        .when('editPricingStrategy', {
          is: true,
          then: yup
            .string()
            .label(t('labelPricingStrategy'))
            .oneOf(Object.values(PricingStrategy))
            .nullable()
            .required(getEmptyErrorMessage(t('labelPricingStrategy'))),
        }),
      margin: numberSchemaWithTransform()
        .label(t('labelMargin'))
        .nullable()
        .when('editMargin', {
          is: true,
          then: numberSchemaWithTransform().label(t('labelMargin')).nullable().required(),
        }),
      priceBasedOn: yup
        .string()
        .label(t('labelPriceBasedOn'))
        .nullable()
        .when('editPriceBasedOn', {
          is: true,
          then: yup.string().label(t('labelPriceBasedOn')).nullable().required(),
        }),
      priceDecimals: numberSchemaWithTransform()
        .label(t('labelPriceDecimals'))
        .nullable()
        .max(PricingHelper.DEFAULT_MAX_DECIMALS, t('errorMaxPriceDecimals'))
        .when('editPriceDecimals', {
          is: true,
          then: numberSchemaWithTransform()
            .label(t('labelPriceDecimals'))
            .nullable()
            .max(PricingHelper.DEFAULT_MAX_DECIMALS, t('errorMaxPriceDecimals'))
            .required(),
        }),
      shipmentType: yup
        .object()
        .label(t('labelShipmentType'))
        .nullable()
        .when('editShipmentType', {
          is: true,
          then: yup.object().label(t('labelShipmentType')).nullable().required(),
        }),
      corporateLocation: yup
        .object()
        .label(t('labelCorporateLocation'))
        .nullable()
        .when('editCorporateLocation', {
          is: true,
          then: yup.object().label(t('labelCorporateLocation')).nullable().required(),
        }),
      taxonomy: yup
        .object()
        .label(t('labelTaxonomy'))
        .nullable()
        .when('editTaxonomy', {
          is: true,
          then: upsertTaxonomyGroupSchemaFields(true, companySettings.taxonomyLevel).taxonomy,
        }),
    })
    .concat(atLeastOneEditField());
};
